import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { JSONContent, generateHTML } from '@tiptap/core';
import { nanoid } from 'nanoid';

import { useOnDemandRichtext } from 'approot/shared/richtext/richtext.hooks';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  Table as TableType,
  EditProps,
  RICHTEXT_SETTINGS,
  TABLE_TYPENAME,
} from './table.constants';
import TableSettingsRow from './table-settings-row';
import TableSettingsColumn from './table-settings-column';
import ActivityBuilderRichtextToolbar from 'approot/activity-builder/activity-builder-richtext-toolbar';
import {
  getExtensions,
  isRichtextEmpty,
} from 'approot/shared/richtext/richtext.utils';
import { SubActivityEdit } from '../sub-activity/sub-activity-edit';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { useTableResize } from './table-edit.hooks';
import {
  getEditableCellsRowHeight,
  getTableHeaderAndBodyRows,
} from './table-view.utils';
import { ColGroups } from './table-common';
import * as Styled from './table.style';
import { useInputFieldState } from '../component.hooks';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  getSuggestedAnswerPayload,
  useBatchSuggestedAnswersContext,
} from '../shared/batch-suggested-answers.context';
import {
  AnswerMap,
  getSuggestedAnswersInput,
  getTableAnswerKey,
  getTableAnswerMap,
} from '../shared/logic/table-answer-map';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import { getRichtextValue } from '../shared/logic/get-richtext-value';
import { TableCellEditView } from './table-edit.style';
import { getComponentSelectorId } from '../shared/logic/get-component-selector-id';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

type EditingCell = { id: string; cellIndex: number; rowIndex: number };

const TableEdit = ({
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  sectionId,
  id,
  type,
  sort,
  version,
}: EditProps) => {
  const { table } = properties;

  const [tableWrapperRef, setTableWrapperRef] = useState<HTMLDivElement | null>(
    null
  );
  const editingCell = useRef<EditingCell | undefined>(undefined);
  const [isInlineRowMenuActive, setIsInlineRowMenuActive] =
    useState<boolean>(false);
  const [isInlineColMenuActive, setIsInlineColMenuActive] =
    useState<boolean>(false);
  const [keyPrefix, setKeyPrefix] = useState<string>(nanoid());

  const { activeComponent, slideScaleRatio } = useActivityBuilderContext();
  const { theme } = useActivityBuilderTheme();

  const answersMap = useMemo(
    () => getTableAnswerMap(suggestedAnswerProperties?.suggestedAnswers || []),
    [suggestedAnswerProperties?.suggestedAnswers]
  );

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { updateSuggestedAnswerApollo } = useBatchSuggestedAnswersContext();

  const handleUpdateTable = useCallback(
    (updatedTable: TableType, answerMap?: AnswerMap) => {
      const answersInput = answerMap
        ? getSuggestedAnswersInput(answerMap, suggestedAnswerProperties)
        : null;

      if (answersInput) {
        updateSuggestedAnswerApollo({
          componentId: id,
          input: answersInput,
          typename: TABLE_TYPENAME,
        });
      }

      const table = {
        isReadOnly: updatedTable.isReadOnly,
        showColumnHeaders: updatedTable.showColumnHeaders,
        showRowHeaders: updatedTable.showRowHeaders,
        columnWidths: updatedTable.columnWidths,
        rows: [
          ...updatedTable.rows.map(r => {
            return {
              ...r,
              cells: r.cells.map(c => {
                return {
                  ...c,
                  value: c.value,
                };
              }),
            };
          }),
        ],
      };

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            type,
            properties: {
              table,
            },
            sort,
          },
          ...(answersInput
            ? getSuggestedAnswerPayload(answersInput)
            : undefined),
        },
        properties: {
          ...properties,
          table: {
            __typename: 'TableComponentMeta',
            ...table,
          },
        },
        typename: TABLE_TYPENAME,
      });
    },
    [
      properties,
      sectionId,
      id,
      type,
      sort,
      suggestedAnswerProperties,
      updateComponentProperties,
      updateSuggestedAnswerApollo,
    ]
  );

  const handleUpdateWidths = useCallback(
    (columnWidths: number[], table: TableType) => {
      const updatedTableData = cloneDeep(table);
      updatedTableData.columnWidths = columnWidths;
      handleUpdateTable(updatedTableData);
    },
    [handleUpdateTable]
  );

  const {
    proportionalWidths,
    onTableDownEvent,
    setTableColumnWidths,
    handleResizeHandleKeyDown,
  } = useTableResize({
    containerElement: tableWrapperRef,
    slideScaleRatio,
    handleUpdate: handleUpdateWidths,
  });

  const updateRowCell = useCallback(
    (rowIndex: number, cellIndex: number, newValue: JSONContent) => {
      const updatedTableData = cloneDeep(table);

      updatedTableData.rows[rowIndex].cells[cellIndex] = {
        ...updatedTableData.rows[rowIndex].cells[cellIndex],
        value: newValue,
      };

      handleUpdateTable(updatedTableData);
    },
    [table, handleUpdateTable]
  );

  const handleUpdateCellRichtext = useCallback(
    (json: JSONContent) => {
      if (editingCell.current) {
        updateRowCell(
          editingCell.current.rowIndex,
          editingCell.current.cellIndex,
          json
        );
      }
    },
    [editingCell, updateRowCell]
  );

  const isComponentActive = activeComponent?.id === id;
  const extensions = useMemo(() => getExtensions(RICHTEXT_SETTINGS), []);

  const { tableHeaderRow, tableBodyRows } = useMemo(
    () => getTableHeaderAndBodyRows(table),
    [table]
  );

  const { setEditorStateToUpdate } = useInputFieldState<JSONContent>({
    onChange: handleUpdateCellRichtext,
  });

  const { createEditor, editor, showToolbar, toolbarRef } = useOnDemandRichtext(
    {
      onChange: json => setEditorStateToUpdate(json),
      settings: RICHTEXT_SETTINGS,
    }
  );

  const initialiseEditor = useCallback(
    (cell: EditingCell, value: JSONContent) => {
      createEditor(value);
      editingCell.current = cell;
    },
    [createEditor]
  );

  // initialise the table resize widths
  useEffect(() => {
    if (tableWrapperRef) {
      setTableColumnWidths(table.columnWidths);
    }
  }, [tableWrapperRef, table.columnWidths, setTableColumnWidths]);

  // clear the active cell if the component is not active
  useEffect(() => {
    if (!isComponentActive && editingCell.current) {
      editingCell.current = undefined;
    }
  }, [isComponentActive]);

  return (
    <SubActivityEdit
      id={id}
      component={{
        id,
        type,
        sort,
        properties,
        sectionId,
        language: LANGUAGE_CODE_EN_AU,
        version,
        __typename: TABLE_TYPENAME,
      }}
      subActivityDetails={subActivityProperties}
    >
      {() => (
        <Styled.TableWrapper
          ref={setTableWrapperRef}
          id={getComponentSelectorId(id)}
        >
          <Styled.TableHeaderControls>
            <ColGroups columnWidths={proportionalWidths} />
            <tbody>
              <Styled.TableRow>
                {(tableHeaderRow?.cells || tableBodyRows[0].cells).map(
                  (_, index) => (
                    <Styled.TableHeaderControlsCell key={index}>
                      {index > 0 && (
                        <TooltipWrapper hoverText="Resize column">
                          <Styled.TableResizeHandle
                            type="button"
                            key={index}
                            $isVisible={isComponentActive}
                            onKeyDown={e =>
                              handleResizeHandleKeyDown(
                                e,
                                index,
                                tableWrapperRef?.offsetWidth || 1000,
                                table
                              )
                            }
                            onPointerDown={e =>
                              onTableDownEvent(
                                e,
                                index,
                                tableWrapperRef?.offsetWidth || 1000,
                                table
                              )
                            }
                          />
                        </TooltipWrapper>
                      )}
                    </Styled.TableHeaderControlsCell>
                  )
                )}
              </Styled.TableRow>
            </tbody>
          </Styled.TableHeaderControls>
          <Styled.Table
            cellSpacing="0"
            key={`${keyPrefix}-rows-${table.rows.length}-cols`}
          >
            <ColGroups columnWidths={proportionalWidths} />
            <thead>
              {table.showRowHeaders && (
                <Styled.TableRow
                  $isRowActive={
                    isInlineRowMenuActive && editingCell.current?.rowIndex === 0
                  }
                >
                  {tableHeaderRow?.cells.map((header, index) => {
                    const cellKey = `${keyPrefix}-row-0-cell-${index}`;
                    const noEditor = cellKey !== editingCell.current?.id;

                    const answer = getRichtextAnswer(
                      answersMap?.[getTableAnswerKey(0, index)]
                    );

                    return (
                      <Styled.TableCell
                        key={cellKey}
                        id={cellKey}
                        $highlight
                        $isTopCell
                        $isColumnActive={
                          isInlineColMenuActive &&
                          editingCell.current?.cellIndex === index
                        }
                      >
                        <Styled.TableColumnSettings
                          $isVisible={
                            isComponentActive &&
                            editingCell.current?.cellIndex === index
                          }
                        >
                          <TableSettingsColumn
                            colIndex={index}
                            table={table}
                            answerMap={answersMap}
                            onMenuToggle={isOpen => {
                              setIsInlineRowMenuActive(false);
                              setIsInlineColMenuActive(isOpen);
                            }}
                            handleUpdateTable={(table, answers) => {
                              setKeyPrefix(nanoid());
                              handleUpdateTable(table, answers);
                            }}
                          />
                        </Styled.TableColumnSettings>
                        {index === 0 && (
                          <Styled.TableRowSettings
                            $isVisible={
                              isComponentActive &&
                              editingCell.current?.rowIndex === index
                            }
                          >
                            <TableSettingsRow
                              rowIndex={0}
                              table={table}
                              answerMap={answersMap}
                              onMenuToggle={isOpen => {
                                setIsInlineColMenuActive(false);
                                setIsInlineRowMenuActive(isOpen);
                              }}
                              handleUpdateTable={(table, answers) => {
                                setKeyPrefix(nanoid());
                                handleUpdateTable(table, answers);
                              }}
                            />
                          </Styled.TableRowSettings>
                        )}

                        {answer ? (
                          <TooltipWrapper
                            hoverText="This cell contains an answer. Edit it in Teaching Notes."
                            multiline
                          >
                            <TableCellEditView
                              $isAnswer
                              $highlight
                              $baseFontSize={theme.font.medium.size}
                              $rows={getEditableCellsRowHeight(
                                tableHeaderRow?.cells
                              )}
                              $baseLineHeight={theme.font.small.lineHeight}
                              dangerouslySetInnerHTML={{
                                __html: getRichtextValue(answer, extensions),
                              }}
                            />
                          </TooltipWrapper>
                        ) : (
                          <Styled.TableCellWrapper
                            onClick={() => {
                              setIsInlineColMenuActive(false);
                              setIsInlineRowMenuActive(false);
                              if (noEditor) {
                                initialiseEditor(
                                  {
                                    id: cellKey,
                                    cellIndex: index,
                                    rowIndex: 0,
                                  },
                                  header.value
                                );
                              }
                            }}
                          >
                            {editingCell.current?.id === cellKey && editor ? (
                              <Styled.TableCellEditor
                                editor={editor}
                                toolbarRef={toolbarRef}
                              />
                            ) : (
                              <>
                                <Styled.TableCellView
                                  $highlight
                                  $baseFontSize={theme.font.medium.size}
                                  dangerouslySetInnerHTML={{
                                    __html: getRichtextValue(
                                      header.value,
                                      extensions
                                    ),
                                  }}
                                />
                                <Styled.TableCellEditTrigger type="button" />
                              </>
                            )}
                          </Styled.TableCellWrapper>
                        )}
                      </Styled.TableCell>
                    );
                  })}
                </Styled.TableRow>
              )}
            </thead>
            <tbody>
              {tableBodyRows.map((row, tableBodyIndex) => {
                const rowIndex = table.showRowHeaders
                  ? tableBodyIndex + 1
                  : tableBodyIndex;
                return (
                  <Styled.TableRow
                    key={`${keyPrefix}-row-${rowIndex}`}
                    id={`${keyPrefix}-row-${rowIndex}`}
                    $isRowActive={
                      isInlineRowMenuActive &&
                      editingCell.current?.rowIndex === rowIndex
                    }
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const cellKey = `${keyPrefix}-row-${rowIndex}-cell-${cellIndex}`;
                      const noEditor = cellKey !== editingCell.current?.id;

                      const answer = getRichtextAnswer(
                        answersMap?.[getTableAnswerKey(rowIndex, cellIndex)]
                      );

                      return (
                        <Styled.TableCell
                          // this needs to be unique so when row headers are added, the cell values are moved over correctly
                          key={cellKey}
                          id={cellKey}
                          $highlight={
                            table.showColumnHeaders && cellIndex === 0
                          }
                          $isColumnActive={
                            isInlineColMenuActive &&
                            editingCell.current?.cellIndex === cellIndex
                          }
                          $isTopCell={rowIndex === 0}
                          $isLastCell={
                            rowIndex ===
                            (table.showRowHeaders
                              ? tableBodyRows.length
                              : tableBodyRows.length - 1)
                          }
                        >
                          {rowIndex === 0 && (
                            <Styled.TableColumnSettings
                              $isVisible={
                                isComponentActive &&
                                editingCell.current?.cellIndex === cellIndex
                              }
                            >
                              <TableSettingsColumn
                                colIndex={cellIndex}
                                table={table}
                                answerMap={answersMap}
                                onMenuToggle={isOpen => {
                                  setIsInlineRowMenuActive(false);
                                  setIsInlineColMenuActive(isOpen);
                                }}
                                handleUpdateTable={(table, answers) => {
                                  setKeyPrefix(nanoid());
                                  handleUpdateTable(table, answers);
                                }}
                              />
                            </Styled.TableColumnSettings>
                          )}
                          {cellIndex === 0 && (
                            <Styled.TableRowSettings
                              $isVisible={
                                isComponentActive &&
                                editingCell.current?.rowIndex === rowIndex
                              }
                            >
                              <TableSettingsRow
                                rowIndex={rowIndex}
                                table={table}
                                answerMap={answersMap}
                                onMenuToggle={isOpen => {
                                  setIsInlineColMenuActive(false);
                                  setIsInlineRowMenuActive(isOpen);
                                }}
                                handleUpdateTable={(table, answers) => {
                                  setKeyPrefix(nanoid());
                                  handleUpdateTable(table, answers);
                                }}
                              />
                            </Styled.TableRowSettings>
                          )}
                          {answer ? (
                            <TooltipWrapper
                              hoverText="This cell contains an answer. Edit it in Teaching Notes."
                              multiline
                            >
                              <TableCellEditView
                                $isAnswer
                                $rows={getEditableCellsRowHeight(row.cells)}
                                $baseLineHeight={theme.font.small.lineHeight}
                                $highlight={
                                  table.showColumnHeaders && cellIndex === 0
                                }
                                $baseFontSize={theme.font.medium.size}
                                dangerouslySetInnerHTML={{
                                  __html: getRichtextValue(answer, extensions),
                                }}
                              />
                            </TooltipWrapper>
                          ) : (
                            <Styled.TableCellWrapper
                              onClick={() => {
                                setIsInlineColMenuActive(false);
                                setIsInlineRowMenuActive(false);
                                if (noEditor) {
                                  initialiseEditor(
                                    {
                                      id: cellKey,
                                      rowIndex: rowIndex,
                                      cellIndex,
                                    },
                                    cell.value
                                  );
                                }
                              }}
                            >
                              {editingCell.current?.id === cellKey && editor ? (
                                <Styled.TableCellEditor
                                  editor={editor}
                                  toolbarRef={toolbarRef}
                                />
                              ) : (
                                <>
                                  <Styled.TableCellView
                                    $highlight={
                                      table.showColumnHeaders && cellIndex === 0
                                    }
                                    $baseFontSize={theme.font.medium.size}
                                    dangerouslySetInnerHTML={{
                                      __html: isRichtextEmpty(cell.value)
                                        ? ''
                                        : generateHTML(cell.value, extensions),
                                    }}
                                  />
                                  <Styled.TableCellEditTrigger
                                    type="button"
                                    data-selector="table-cell"
                                  />
                                </>
                              )}
                            </Styled.TableCellWrapper>
                          )}
                        </Styled.TableCell>
                      );
                    })}
                  </Styled.TableRow>
                );
              })}
            </tbody>
          </Styled.Table>

          <ActivityBuilderRichtextToolbar
            editor={editor}
            toolbarRef={toolbarRef}
            showToolbar={showToolbar}
            canChangeBlockType={false}
          />
        </Styled.TableWrapper>
      )}
    </SubActivityEdit>
  );
};

export default TableEdit;
